import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, ChevronRight, DeleteOutline, EditOutlined, Refresh, UploadFile } from '@mui/icons-material';
import type { AssistantDefinition, IFileListItem } from '../../client/SmartAssistantBackendClient';
import { FileTree } from '../files/FileTree';
import { ProfileDiffView } from '../profile/ProfileDiffView';
import { useAdminContext } from '../../contexts/AdminContext';
import { ProfileService } from '../../services/ProfileService';
import { useLoadingContext } from '../../contexts/LoadingContext';

type AdminDrawerProps = {
  adminOpen: boolean;
  onClose: () => void;
};

export const AdminDrawer: React.FC<AdminDrawerProps> = ({ adminOpen, onClose }) => {
  const { consumer, adminConfigState: adminConfig, assistantState } = useAdminContext();
  const { loading, loadingText, beginLoadingKey } = useLoadingContext();
  const {
    documentAssistantDrafts,
    assistantSaveState,
    adminAssistantFilesOpen,
    setAdminAssistantFilesOpen,
    addAssistantDraft,
    openAssistantModal,
    handleDeleteAssistantDraft,
    listFilesForAssistant,
    embedFileForAssistant,
    deleteFileForAssistant,
    renameFolderForAssistant,
    deleteFolderForAssistant,
    uploadFilesToFolderForAssistant,
  } = assistantState;
  const [openAiKeyDraft, setOpenAiKeyDraft] = useState('');
  const [profileFileDraft, setProfileFileDraft] = useState('');
  const [systemPromptDraft, setSystemPromptDraft] = useState('');
  const [profileSystemPromptDraft, setProfileSystemPromptDraft] = useState('');
  const [profileSuggestionStatus, setProfileSuggestionStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [profileSuggestionError, setProfileSuggestionError] = useState<string | null>(null);
  const [profileSuggestionOriginal, setProfileSuggestionOriginal] = useState('');
  const [profileSuggestionDraft, setProfileSuggestionDraft] = useState('');
  const [profileSuggestionFile, setProfileSuggestionFile] = useState<File | null>(null);
  const profileUploadInputRef = useRef<HTMLInputElement | null>(null);
  const profileSuggestionUploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!adminOpen) return;
    setOpenAiKeyDraft('');
    setProfileFileDraft((adminConfig.value?.profileFile ?? '').trim());
    setSystemPromptDraft(adminConfig.value?.systemPrompt ?? '');
    setProfileSystemPromptDraft(adminConfig.value?.profileSystemPrompt ?? '');
  }, [adminOpen, adminConfig.value?.profileFile, adminConfig.value?.systemPrompt, adminConfig.value?.profileSystemPrompt]);

  const normalizeProfileValue = (value: string) => value.trim().replace(/\\/g, '/');
  const resolveProfileDisplayName = useCallback((value: string) => {
    const clean = normalizeProfileValue(value);
    if (!clean) return '';
    try {
      const url = new URL(clean);
      const name = url.pathname.split('/').pop();
      return name || clean;
    } catch {
      return clean.includes('/') ? (clean.split('/').pop() ?? clean) : clean;
    }
  }, []);
  const profileSelectOptions = useMemo(() => {
    const options = [...adminConfig.profileFiles];
    const selected = normalizeProfileValue(profileFileDraft);
    if (selected && !options.some((item) => (item.fileUrl ?? '').trim() === selected)) {
      options.unshift({
        fileName: resolveProfileDisplayName(selected),
        fileUrl: selected,
      } as IFileListItem);
    }
    return options;
  }, [adminConfig.profileFiles, profileFileDraft, resolveProfileDisplayName]);
  const hasProfileSuggestion = Boolean(profileSuggestionOriginal || profileSuggestionDraft);
  const normalizeSystemPrompt = (value?: string | null) => (value ?? '').trim();
  const canSaveAdminConfig = (openAiKeyDraft.trim().length > 0
    || normalizeProfileValue(profileFileDraft) !== normalizeProfileValue(adminConfig.value?.profileFile ?? '')
    || normalizeSystemPrompt(adminConfig.value?.systemPrompt) !== normalizeSystemPrompt(systemPromptDraft)
    || normalizeSystemPrompt(adminConfig.value?.profileSystemPrompt) !== normalizeSystemPrompt(profileSystemPromptDraft))
    && !loading;

  const uploadProfileFile = async (file: File) => {
    const stopLoading = beginLoadingKey('profileUpload');
    try {
      const token = await consumer.getUserToken();
      const result = await ProfileService.uploadProfileFile(token as string, file);
      await adminConfig.handleReloadProfileFiles();
      const nextUrl = result?.fileUrl || '';
      const nextValue = nextUrl || result?.fileName || file.name;
      setProfileFileDraft(nextValue);
    } finally {
      stopLoading();
    }
  };

  const requestProfileSuggestion = async () => {
    setProfileSuggestionStatus('loading');
    setProfileSuggestionError(null);
    const stopLoading = beginLoadingKey('profileSuggestion');
    try {
      const token = await consumer.getUserToken();
      const result = await ProfileService.requestSuggestion(token as string, profileSuggestionFile);
      setProfileSuggestionOriginal(result?.originalProfile ?? '');
      setProfileSuggestionDraft(result?.suggestedProfile ?? '');
      setProfileSuggestionStatus('loaded');
    } catch (err: any) {
      console.error(err);
      setProfileSuggestionError(err?.message ?? 'Profilvorschlag fehlgeschlagen.');
      setProfileSuggestionStatus('error');
    } finally {
      stopLoading();
    }
  };
  return (
  <Drawer
    anchor="right"
    open={adminOpen}
    onClose={onClose}
    ModalProps={{ keepMounted: false }}
    PaperProps={{
      sx: (theme) => ({
        width: { xs: '100%', sm: '90vw' },
        minWidth: { sm: 520 },
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${theme.palette.divider}`,
      }),
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Administration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assistenten, Files & Content-Embedding
        </Typography>
      </Box>
      <IconButton
        onClick={onClose}
        sx={{ color: 'text.secondary' }}
      >
        <ChevronRight />
      </IconButton>
    </Box>

    <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

    <Box p={3} display="grid" gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" color="text.secondary">
          Frontend-Konfiguration
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => adminConfig.onReload()}
          disabled={loading}
        >
          Neu laden
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          OpenAI Key:
        </Typography>
        <Chip
          label={adminConfig.value?.openAiTokenExists ? 'vorhanden' : 'nicht gesetzt'}
          variant="outlined"
          sx={(theme) => ({ borderColor: theme.palette.divider })}
        />
      </Box>

      <TextField
        type="password"
        label="OpenAI Key setzen"
        value={openAiKeyDraft}
        onChange={(ev) => setOpenAiKeyDraft(ev.target.value)}
        helperText={openAiKeyDraft
          ? 'Neuer Key wird beim Speichern gesetzt.'
          : 'Leer lassen, um den aktuellen Key beizubehalten.'}
      />

      <Box display="grid" gap={1}>
        <TextField
          select
          fullWidth
          label="Unternehmensprofil"
          value={profileFileDraft}
          onChange={(ev) => {
            setProfileFileDraft(String(ev.target.value));
          }}
          helperText={loading
            ? (loadingText || 'Profile werden geladen …')
            : adminConfig.profileFilesError
              ? `Profile konnten nicht geladen werden${adminConfig.profileFilesError ? `: ${adminConfig.profileFilesError}` : '.'}`
              : 'Wähle ein Profil aus oder lade ein neues hoch.'}
        >
          <MenuItem value="">
            Kein Profil ausgewählt
          </MenuItem>
          {profileSelectOptions.map((item) => (
            <MenuItem key={item.fileUrl ?? item.fileName} value={item.fileUrl ?? item.fileName ?? ''}>
              {item.fileName ?? item.blobPath ?? item.fileUrl ?? 'Profil'}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => profileUploadInputRef.current?.click()}
          >
            Profil hochladen
          </Button>
          <Button
            variant="text"
            onClick={() => adminConfig.handleReloadProfileFiles()}
            disabled={loading}
          >
            Profil-Liste aktualisieren
          </Button>
        </Stack>
        <input
          ref={profileUploadInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={async (ev) => {
            const file = ev.target.files?.[0];
            if (!file) return;
            try {
              await uploadProfileFile(file);
            } catch (err: any) {
              console.error(err);
            } finally {
              if (profileUploadInputRef.current) {
                profileUploadInputRef.current.value = '';
              }
            }
          }}
        />
      </Box>

      <TextField
        label="System-Prompt (global)"
        value={systemPromptDraft}
        onChange={(ev) => {
          setSystemPromptDraft(ev.target.value);
        }}
        helperText="Gilt für alle Antworten der Dokumenten-Analyse (zusätzlich zu den Prompt-Definitionen)."
        multiline
        minRows={6}
      />
      <TextField
        label="System-Prompt (Profil)"
        value={profileSystemPromptDraft}
        onChange={(ev) => {
          setProfileSystemPromptDraft(ev.target.value);
        }}
        helperText="Wird eingefügt, wenn ein Unternehmensprofil aktiv ist."
        multiline
        minRows={4}
      />

      <Box display="grid" gap={1.5}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle2" color="text.secondary">
            KI-Profilagent
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={requestProfileSuggestion}
            disabled={profileSuggestionStatus === 'loading'}
          >
            Vorschlag erstellen
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Erstellt einen Profilvorschlag auf Basis des aktuellen Profils und der Eckdaten aller konfigurierten Vectorstores.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => profileSuggestionUploadInputRef.current?.click()}
          >
            Profil-Datei für Vorschlag wählen
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {profileSuggestionFile ? profileSuggestionFile.name : 'Keine Datei ausgewählt'}
          </Typography>
        </Stack>
        <input
          ref={profileSuggestionUploadInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(ev) => {
            const file = ev.target.files?.[0] ?? null;
            setProfileSuggestionFile(file);
            if (ev.target) {
              ev.target.value = '';
            }
          }}
        />

        {profileSuggestionStatus === 'loading' && (
          <Typography variant="caption" color="text.secondary">
            Profilvorschlag wird erstellt …
          </Typography>
        )}
        {profileSuggestionStatus === 'error' && profileSuggestionError && (
          <Typography variant="caption" color="error">
            {profileSuggestionError}
          </Typography>
        )}

        {hasProfileSuggestion && (
          <Box display="grid" gap={1.5}>
            <TextField
              label="Vorschlag bearbeiten"
              multiline
              minRows={8}
              value={profileSuggestionDraft}
              onChange={(ev) => setProfileSuggestionDraft(ev.target.value)}
            />
            <ProfileDiffView
              originalText={profileSuggestionOriginal}
              suggestedText={profileSuggestionDraft}
            />
          </Box>
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          variant="contained"
          onClick={() => adminConfig.onSave({
            openAiKeyDraft,
            profileFileDraft,
            systemPromptDraft,
            profileSystemPromptDraft,
          })}
          disabled={!canSaveAdminConfig}
          fullWidth
        >
          {loading ? 'Speichern …' : 'Konfiguration speichern'}
        </Button>
      </Stack>

      {loading && loadingText && (
        <Typography variant="caption" color="text.secondary">
          {loadingText}
        </Typography>
      )}
      {adminConfig.error && (
        <Typography variant="caption" color="error">
          {adminConfig.error}
        </Typography>
      )}
    </Box>

    <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

    <Box p={3} display="grid" gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Assistenten
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutline />}
          onClick={addAssistantDraft}
        >
          Assistent hinzufügen
        </Button>
      </Box>

      {documentAssistantDrafts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Noch keine Assistenten vorhanden.
        </Typography>
      ) : (
        <Box display="grid" gap={1.5}>
          {documentAssistantDrafts.map((assistant: AssistantDefinition, index: number) => {
            const internalName = assistant.internalName?.trim() ?? '';
            const nameLabel = assistant.name?.trim() || internalName || `Assistent ${index + 1}`;
            const promptCount = assistant.promptDefinitions?.length ?? 0;
            const assistantKey = assistant.id ?? `draft-${index}`;
            const saveState = assistantSaveState[assistantKey] ?? { status: 'idle', message: null };
            const filesOpen = adminAssistantFilesOpen[assistantKey] ?? false;
            return (
              <Paper
                key={assistantKey}
                sx={(theme) => ({
                  ml: { xs: 0, sm: 4 },
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                })}
              >
                <Box flexGrow={1} display="grid" gap={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box display="grid" gap={0.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {`Assistent: ${nameLabel}`}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {assistant.isDefault && (
                          <Chip label="Standard" size="small" color="primary" />
                        )}
                        {internalName ? (
                          <Chip label={internalName} size="small" variant="outlined" sx={(theme) => ({ borderColor: theme.palette.divider })} />
                        ) : (
                          <Chip label="Internal Name fehlt" size="small" variant="outlined" sx={(theme) => ({ borderColor: theme.palette.divider })} />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {promptCount} Prompts
                        </Typography>
                      </Box>
                      {assistant.topics?.trim() && (
                        <Typography variant="body2" color="text.secondary">
                          {assistant.topics}
                        </Typography>
                      )}
                      {saveState.message && (
                        <Typography
                          variant="caption"
                          color={saveState.status === 'error' ? 'error' : 'text.secondary'}
                        >
                          {saveState.message}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => setAdminAssistantFilesOpen((prev: Record<string, boolean>) => ({
                          ...prev,
                          [assistantKey]: !filesOpen,
                        }))}
                        disabled={!internalName}
                      >
                        {filesOpen ? 'Dateien ausblenden' : 'Dateien anzeigen'}
                      </Button>
                      <IconButton
                        onClick={() => openAssistantModal(index)}
                        sx={{ color: 'text.secondary' }}
                        aria-label={`Assistent bearbeiten: ${nameLabel}`}
                      >
                        <EditOutlined />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteAssistantDraft(index)}
                        disabled={saveState.status === 'saving' || saveState.status === 'deleting'}
                        sx={{ color: 'text.secondary' }}
                        aria-label={`Assistent löschen: ${nameLabel}`}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={filesOpen} timeout="auto" unmountOnExit>
                    <Box mt={1}>
                      <Divider sx={(theme) => ({ borderColor: theme.palette.divider, mb: 1.5 })} />
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>
                        Dateien & Ordner
                      </Typography>
                      {internalName ? (
                        <FileTree
                          rootKey={assistantKey}
                          loadChildren={(path) => listFilesForAssistant(internalName, path)}
                          onEmbed={(item) => embedFileForAssistant(internalName, item)}
                          onDelete={(item) => deleteFileForAssistant(internalName, item)}
                          onRenameFolder={(src, target) => renameFolderForAssistant(internalName, src, target)}
                          onDeleteFolder={(folder) => deleteFolderForAssistant(internalName, folder)}
                          onUploadToFolder={(folder, files) => uploadFilesToFolderForAssistant(internalName, folder, files)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Bitte zuerst einen Internal Name setzen.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>

                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  </Drawer>
  );
};
